import { isWeb } from "@helpers/platform";
import { useAppTheme } from "@hooks/useAppTheme";
import { formatCount } from "@memorang/helpers/common";
import CustomBottomSheetTextInput from "components/CustomBottomSheetTextInput";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { View } from "react-native";
import { Text, TextInput } from "react-native-paper";

const MAX_LENGTH = 6000;
const MIN_HEIGHT = 150;
const MAX_HEIGHT = 400;

type Props = {
	value?: string;
	onChangeText: (value: string) => void;
	disabled?: boolean;
};

const Writing = ({ value, onChangeText, disabled }: Props) => {
	const theme = useAppTheme();
	const { t } = useTranslation();
	const [inputHeight, setInputHeight] = useState(MIN_HEIGHT);
	const containerRef = useRef<View>(null);

	const wordCount = value?.trim().split(/\s+/).filter(Boolean).length ?? 0;
	const placeholder = t("writing.placeholder");

	// biome-ignore lint/correctness/useExhaustiveDependencies: value triggers re-measurement of textarea height
	useEffect(() => {
		if (!(isWeb && containerRef.current)) {
			return;
		}
		const el = containerRef.current as unknown as HTMLElement;
		const textarea = el.querySelector("textarea");
		if (!textarea) {
			return;
		}

		textarea.style.height = `${MIN_HEIGHT}px`;
		const newHeight = Math.min(
			Math.max(textarea.scrollHeight, MIN_HEIGHT),
			MAX_HEIGHT,
		);
		textarea.style.height = `${newHeight}px`;
		setInputHeight(newHeight);
	}, [value]);

	if (isWeb) {
		return (
			<View ref={containerRef} style={{ gap: 4 }}>
				<TextInput
					placeholder={placeholder}
					disabled={disabled}
					onChangeText={onChangeText}
					contentStyle={{
						minWidth: 300,
						height: inputHeight,
						maxHeight: MAX_HEIGHT,
						overflow: "scroll",
					}}
					placeholderTextColor={theme.colors.onSurfaceDisabled}
					multiline
					mode="outlined"
					value={value}
					autoComplete="off"
					autoCorrect={false}
					spellCheck={false}
					autoCapitalize="none"
					maxLength={MAX_LENGTH}
				/>
				<Text variant="bodySmall">
					{t("writing.wordCount", {
						count: wordCount,
						word: formatCount(t("common.word"), wordCount),
					})}
				</Text>
			</View>
		);
	}

	return (
		<CustomBottomSheetTextInput
			value={value}
			onChangeText={onChangeText}
			placeholder={placeholder}
			wordCount={wordCount}
			maxLength={MAX_LENGTH}
			disabled={disabled}
		/>
	);
};

export default Writing;
